import React, { useMemo, useState,useEffect, useRef, useCallback, useLayoutEffect } from 'react'
import * as  THREE from 'three'

import { const_var, params } from '../../../../../../redux/reducers/reducer'

import { CSG } from '../../../../../../assets/libs/csg-lib'
import { delModel, extrudeSettings, railLoftModel, railModel } from '../../../../../../utils/Functions'
import { wallNameArrForValid } from '../../../../../../utils/Validation'
import { MaterialHighlightTruss, MaterialTruss } from '../../../../CustomMaterial'

const Legs = ({ normalMap, envMap, overlapTrussArr }) => {
    const obj_upperTrussNormal = new THREE.Object3D();
    const obj_upperTrussHighlight = new THREE.Object3D();
    const obj_sideSlopeRail = new THREE.Object3D();
    const obj_loftPieceRail = new THREE.Object3D();
    const obj_outerPillar = new THREE.Object3D();
    const obj_innerPillar = new THREE.Object3D();
    const obj_outerPillarHighlight = new THREE.Object3D();
    const obj_connectRail = new THREE.Object3D();
    const obj_connectBottomRail = new THREE.Object3D();

    const ref_upperTrussNormal = useRef();
    const ref_upperTrussHighlight = useRef();
    const ref_sideSlopeRail = useRef();
    const ref_loftPieceRail = useRef();
    const ref_outerPillarHighlight = useRef();
    const ref_outerPillar = useRef();
    const ref_innerPillar = useRef();
    const ref_connectRail = useRef(); 
    const ref_connectBottomRail = useRef();    

    const [isEqualLeftHeight, setIsEqualLeftHeight] = useState(false);
    const [isEqualRightHeight, setIsEqualRightHeight] = useState(false);
    const [isEqualFrontHeight, setIsEqualFrontHeight] = useState(false);
    const [csgLegIdArr, setCsgLegIdArr] = useState([])

    const angle = Math.atan(parseInt(params.b_l_t_r_p) / 12)
    const doorDataArr = const_var.newDoorsArray.filter((item) => (item.userData.wallName === 'L_L_L_W' || item.userData.wallName === 'L_L_L_S_W'))

    const trimThickness = 0.01;
    const railWidth = 0.2;
    const railHeight = 0.3;
    const extraLength = 0.2;
    const cbFrontBackWallOffset = 0.16;
    const edgeSlopeRailLength = 2;
    const edgeSlopeRailAlpha = Math.PI / 5.5;

    const leanRoofLeftHeight = useMemo(() => {return ((params.b_l_t_r_p / 12) * params.lean_p_w)}, [params.b_l_t_r_p, params.lean_p_w]) 
    const leanRoofRightHeight = useMemo(() => {return ((params.b_l_t_r_pR / 12) * params.leanR_p_w)}, [params.b_l_t_r_pR, params.leanR_p_w]) 
    const leanRoofFrontHeight = useMemo(() => {return ((params.b_l_t_r_pF / 12) * params.leanF_p_w) }, [params.b_l_t_r_pF, params.leanF_p_w]) 

    const trimOffsetHeight = (params.p_r_p / 12) * 0.2;
    
    const outInDistance = useMemo(() => {
        return (const_var.leanLegstype === "double") ? 0.21 : 
       (const_var.leanLegstype === "ladder") ? 0.5 : 
       (const_var.leanLegstype === "zigzag") ? 1 : 0;
    }, [const_var.leanLegstype]) 

    const legDistance = useMemo(() => {
        return (const_var.DistanceArr.length > 0 && const_var.DistanceArr[params.p_b_t] != undefined && const_var.DistanceArr[params.p_b_t][params.p_r_s] !== undefined) ? const_var.DistanceArr[params.p_b_t][params.p_r_s] : 5
    }, [const_var.DistanceArr, params.p_b_t, params.p_r_s,params.fourth_center_cost,const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined && const_var.DistanceArr[params.p_b_t][params.p_r_s]])
    
    const offset = useMemo(() => {
        if(params.lean_p_d % legDistance === 0) return 0;
        else return -0.15;
    }, [params.lean_p_d, legDistance])
    
    let count, distance;
        
    const availableSize = parseInt(params.add_storage);
    const initDistance = parseInt(params.add_storage);
    const tempCount = Math.floor(availableSize / initDistance);
    if (availableSize === tempCount * initDistance) {
        count = tempCount;
        distance = initDistance;
    } else if (availableSize - tempCount * initDistance < initDistance / 2) {
        count = tempCount;
        distance = availableSize / count;
    } else {
        count = tempCount + 1;
        distance = availableSize / count;
    }

    const verticalRailLength = useMemo(() =>{
        return Math.sqrt(Math.pow(params.lean_p_w, 2) + Math.pow(params.lean_p_w * (parseInt(params.b_l_t_r_p) / 12), 2)) + (params.p_r_s === 1 ? -0.35 : 0.1)
    }, [params.lean_p_w, parseInt(params.b_l_t_r_p), params.p_r_s])

    const outerRailLength = useMemo(() =>{
        return params.lean_p_h - railWidth / 4 + (params.p_r_s === 1 && -cbFrontBackWallOffset * 2 + 0.5)
    }, [params.lean_p_h, params.p_r_s])

    const innerRailLength = useMemo(() =>{
        return params.lean_p_h + (outInDistance / 2) * parseInt(params.b_l_t_r_p) / 12 + 0.02 + (params.p_r_s === 1 && (const_var.leanLegstype === "ladder" || const_var.leanLegstype === "zigzag") && (const_var.roofRegularDst * 2 - 0.1));
    }, [params.lean_p_h, outInDistance, parseInt(params.b_l_t_r_p), params.p_r_s, const_var.leanLegstype])

    const pillarRailDataArr = useMemo(() => {
        let dataInfoArray = [];
        let tempDataInfo;
        
        for (let i = 0; i <= count; i++) {
            let pos_x;

            if(i === 0 ) pos_x = distance * i - params.lean_p_d / 2;
            else pos_x = distance * i - params.lean_p_d / 2 + offset;

            tempDataInfo = {
                pos_x: pos_x,
                inner_rail: {
                    length: params.p_h + (params.p_w / 2 - Math.abs(pos_x - params.lean_p_pos)) * (params.p_r_p / 12) - railWidth / 2,
                    pos_z: params.p_w / 2,
                },
                outer_rail: {
                    length: outerRailLength,
                    pos_z: params.p_w / 2 + params.lean_p_w - railWidth + 0.05,
                },
                outer_in_rail: {
                    length: innerRailLength,
                    pos_z: params.p_w / 2 + params.lean_p_w - railWidth + 0.05 - outInDistance,
                },
                roof_rail: {
                    length: verticalRailLength,
                    pos_y: (params.lean_p_h + params.lean_p_w * (parseInt(params.b_l_t_r_p) / 12) - railWidth / 2 + (params.p_r_s === 1 ?  const_var.roofRegularDst * 2 + 0.06 : params.p_r_s === 2 ? -0.025 : 0) - (isEqualRightHeight ? 0.14: 0.07)),
                    pos_z: params.p_w / 2 + 0.02,
                    alpha: -(Math.PI / 2 + Math.atan(parseInt(params.b_l_t_r_p) / 12)),
                },
                loft_rail: {
                    length: railHeight,
                    pos_x: pos_x - railHeight / 2,
                    pos_y: params.lean_p_h - railWidth / 4 - cbFrontBackWallOffset * 2 + 0.38,
                    pos_z: params.p_w / 2 + params.lean_p_w - 0.05,
                },
                slope_rail: {
                    length: edgeSlopeRailLength,
                    alpha: edgeSlopeRailAlpha,
                    pos_y: params.lean_p_h + (params.p_r_s === 1 ? const_var.roofRegularDst * 2 : 0) - (edgeSlopeRailLength * Math.cos(edgeSlopeRailAlpha) - (edgeSlopeRailLength * Math.sin(edgeSlopeRailAlpha)) * Math.tan(parseInt(params.b_l_t_r_p) / 12)) - parseInt(params.b_l_t_r_p) / 100 + (params.add_left_front_lean_porch || params.add_right_front_lean_porch ? -0.02 : 0.025),
                    pos_z: params.p_w / 2 + params.lean_p_w - 0.15,
                }
            }

            dataInfoArray.push(tempDataInfo);
        }
        
        return dataInfoArray;
    }, [params.lean_p_d, params.p_h, params.p_w, params.p_r_p, params.lean_p_h, params.p_r_s, params.leftLeanStoragePosition, params.lean_p_w, parseInt(params.b_l_t_r_p), legDistance, isEqualFrontHeight, params.lean_p_pos, outInDistance, const_var.leanLegstype, params.add_left_front_lean_porch, params.lean_p_pos, params.add_right_front_lean_porch, edgeSlopeRailAlpha, distance, availableSize, count, offset])
    
    const connectRailDataArr = useMemo(() => {
        let dataInfoArray = [];
        let count, distance;

        let initDistance = (const_var.leanLegstype === 'zigzag') ? 1 : 2;
        const tempCount = Math.floor(params.lean_p_h / initDistance);
        if (params.lean_p_h === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if (params.lean_p_h - tempCount * initDistance <= initDistance / 2) {
            count = tempCount;
            distance = params.lean_p_h / count;
        } else {
            count = tempCount + 1;
            distance = params.lean_p_h / count;
        }
        const isEven = (i) => {
            return i % 2 === 0;
        }

        for (let i = 0; i < count; i++) {            
            const length = ((i === 0) ? outInDistance : (outInDistance / Math.cos(Math.PI / 4)))
            const pos_z = params.p_w / 2 + params.lean_p_w - railHeight / 2 - (const_var.leanLegstype === 'zigzag' ? (!isEven(i) ? outInDistance : 0) : outInDistance);
            const pos_y = distance * i + railWidth / 2;
            const rotationX = Math.atan((distance) / outInDistance)

            const tempInfoObj = {
                pos_z: pos_z,
                distance,
                pos_y: pos_y,
                length: const_var.leanLegstype === 'zigzag' ? length : outInDistance,
                rotationX: const_var.leanLegstype === 'zigzag' ? isEven(i) ? (i === 0 ? Math.PI : (Math.PI / 2 + rotationX)) : rotationX : 0
            }

            dataInfoArray.push(tempInfoObj);
        }
        const lengthZigzag = outInDistance / Math.cos(Math.PI / 4);
        const lengthLadder = outInDistance;

        return {lengthZigzag, lengthLadder, dataInfoArray}
    }, [params.lean_p_h, params.p_d, params.lean_p_w, params.p_w, const_var.leanLegstype, outInDistance])

    const roofPatternRailDataArr = useMemo(() => {
        let dataInfoArray = [];
        let count, distance, tempDataInfo;
        
        const initDistance = 3;
        const tempCount = Math.floor(params.lean_p_w / initDistance);
        if (params.lean_p_w === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if (params.lean_p_w - tempCount * initDistance <= initDistance / 2) {
            count = tempCount;
            distance = params.lean_p_w / count;
        } else {
            count = tempCount + 1;
            distance = params.lean_p_w / count;
        }

        const initPosZ = 0.5
        const thickness = isEqualLeftHeight ? 0.04 : 0.035
        for (let i = 0; i < count; i++) {
            tempDataInfo = {
                pos_x: -params.lean_p_d / 2,
                pos_y: params.lean_p_h + (params.lean_p_w - distance * i - initPosZ) * (parseInt(params.b_l_t_r_p) / 12) - thickness,
                pos_z: params.p_w / 2 + distance * i + initPosZ,
                length: params.lean_p_d,
                alpha: Math.atan(parseInt(params.b_l_t_r_p) / 12),
            }
            
            dataInfoArray.push(tempDataInfo);
        }
        
        return dataInfoArray;
    }, [params.p_w, params.p_d, params.lean_p_h, params.lean_p_d, parseInt(params.b_l_t_r_p), params.lean_p_w, isEqualLeftHeight])

    const frontBackPillarRailDataArr = useMemo(() => {
        let dataInfoArray = []
        let count, distance = 0;

        const initDistance = legDistance;
        
        const tempCount = Math.floor(params.lean_p_w / initDistance);
        if (params.lean_p_w === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if (params.lean_p_w - tempCount * initDistance <= initDistance / 2) {
            count = tempCount;
            distance = params.lean_p_w / count;
        } else {
            count = tempCount + 1;
            distance = params.lean_p_w / count;
        }
        
        for (let i = 1; i < count; i++) {
            const pos_z = distance * i - params.p_w / 2 - params.lean_p_w;
            const length = params.lean_p_h + (params.lean_p_w + params.p_w / 2 - Math.abs(pos_z)) * (parseInt(params.b_l_t_r_p) / 12) - railWidth / 2 + (params.p_r_s === 1 && const_var.roofRegularDst * 2);
            
            const tempInfoObj ={
                pos_z: pos_z,
                length: length
            }
            
            dataInfoArray.push(tempInfoObj);
        }
        
        return dataInfoArray;
    }, [params.lean_p_w, params.p_w, legDistance, params.lean_p_h, parseInt(params.b_l_t_r_p), params.p_r_s])

    /************** cutting hole using CSG ***************/
    
    const innerDoorModel = new THREE.Mesh(
        new THREE.ExtrudeGeometry(delModel('left', 'leg'), extrudeSettings(2)),
        new THREE.MeshStandardMaterial({ color: 'brown' })
    )
    const innerDoorModel_Temp = innerDoorModel.clone();
    innerDoorModel_Temp.rotateY(Math.PI)
    innerDoorModel_Temp.position.set(-params.lean_p_pos - params.leftLeanStoragePosition, 0, params.p_w / 2 + cbFrontBackWallOffset + 0.5);
    innerDoorModel_Temp.updateMatrixWorld();
    
    const outerDoorModel = new THREE.Mesh(
        new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'leg'), extrudeSettings(2)),
        new THREE.MeshStandardMaterial({ color: 'brown' })
    )
    const outerDoorModel_Temp = outerDoorModel.clone();
    outerDoorModel_Temp.rotateY(Math.PI)
    outerDoorModel_Temp.position.set(-params.lean_p_pos - params.leftLeanStoragePosition, 0, params.p_w / 2 + params.lean_p_w + cbFrontBackWallOffset + 0.5);
    outerDoorModel_Temp.updateMatrixWorld();

    const CSGFrontBackPillarRailModelArr = useMemo(() => {
        let frontModelArr = [];
        let backModelArr = [];

        const modelShape = railModel(railWidth, railHeight);
        
        frontBackPillarRailDataArr.map((item) => {    
            const meshFrontPillarRailModel = new THREE.Mesh(
                new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length)),
                new THREE.MeshStandardMaterial({ color: 'pink' })
            )

            const meshBackPillarRailModel = new THREE.Mesh(
                new THREE.ExtrudeGeometry(modelShape, extrudeSettings(item.length)),
                new THREE.MeshStandardMaterial({ color: 'pink' })
            )

            let meshFrontPillarRailModel_Temp = meshFrontPillarRailModel.clone(); 
            meshFrontPillarRailModel_Temp.position.set(0, 0, -item.pos_z);
            meshFrontPillarRailModel_Temp.rotateX(-Math.PI / 2);
            meshFrontPillarRailModel_Temp.rotateZ(Math.PI / 2);
            meshFrontPillarRailModel_Temp.updateMatrix();
            meshFrontPillarRailModel_Temp.updateMatrixWorld();

            let meshBackPillarRailModel_Temp = meshBackPillarRailModel.clone(); 
            meshBackPillarRailModel_Temp.position.set(0, 0, -item.pos_z);
            meshBackPillarRailModel_Temp.rotateX(-Math.PI / 2);
            meshBackPillarRailModel_Temp.rotateZ(Math.PI / 2);
            meshBackPillarRailModel_Temp.updateMatrix();
            meshBackPillarRailModel_Temp.updateMatrixWorld();

            const outerFrontDoorModel = new THREE.Mesh(
                new THREE.ExtrudeGeometry(delModel('leftLeanStorageFront', 'leg'), extrudeSettings(2)),
                new THREE.MeshStandardMaterial({ color: 'brown' })
            )

            const outerbackDoorModel = new THREE.Mesh(
                new THREE.ExtrudeGeometry(delModel('leftLeanStorageBack', 'leg'), extrudeSettings(2)),
                new THREE.MeshStandardMaterial({ color: 'brown' })
            )

            const outerFrontDoorModel_Temp = outerFrontDoorModel.clone();
            outerFrontDoorModel_Temp.rotateY(Math.PI / 2)
            outerFrontDoorModel_Temp.position.set(-1, 0, 0);
            outerFrontDoorModel_Temp.updateMatrixWorld();
        
            const outerBackDoorModel_Temp = outerbackDoorModel.clone();
            outerBackDoorModel_Temp.rotateY(Math.PI / 2)
            outerBackDoorModel_Temp.position.set(-1, 0, 0);
            outerBackDoorModel_Temp.updateMatrixWorld();
            
            const meshFrontPillarRailModel_CSG = CSG.fromMesh(meshFrontPillarRailModel_Temp);
            const meshBackPillarRailModel_CSG = CSG.fromMesh(meshBackPillarRailModel_Temp);
            const outerFrontDoorModel_CSG = CSG.fromMesh(outerFrontDoorModel_Temp);
            const outerBackDoorModel_CSG = CSG.fromMesh(outerBackDoorModel_Temp);

            const meshFrontPillarDelModel_CSG = meshFrontPillarRailModel_CSG.subtract(outerFrontDoorModel_CSG);
            const meshBackPillarDelModel_CSG = meshBackPillarRailModel_CSG.subtract(outerBackDoorModel_CSG);

            const CSGFrontPillarRailModel = CSG.toMesh(
                meshFrontPillarDelModel_CSG,
                new THREE.Matrix4(),
                new THREE.MeshStandardMaterial({ color: 'blue' })
            )

            const CSGBackPillarRailModel = CSG.toMesh(
                meshBackPillarDelModel_CSG,
                new THREE.Matrix4(),
                new THREE.MeshStandardMaterial({ color: 'blue' })
            )

            frontModelArr.push(CSGFrontPillarRailModel);
            backModelArr.push(CSGBackPillarRailModel);
            
            return null;   
            
        })
        return {frontModelArr, backModelArr}
    }, [frontBackPillarRailDataArr, const_var.newDoorsArray])

    const CSGFrontBackBottomRailData = useMemo(() => {

        const modelShape = railModel(railHeight, railWidth);

        const meshBottomRailModel = new THREE.Mesh(
            new THREE.ExtrudeGeometry(modelShape, extrudeSettings(params.lean_p_w)),
            new THREE.MeshStandardMaterial({ color: 'pink' })
        )

        let meshFrontBottomRailModel_Temp = meshBottomRailModel.clone();    
        meshFrontBottomRailModel_Temp.position.set(0, railWidth / 2, params.p_w / 2 - railWidth / 2);
        meshFrontBottomRailModel_Temp.updateMatrix();
        meshFrontBottomRailModel_Temp.updateMatrixWorld();

        let meshBackBottomRailModel_Temp = meshBottomRailModel.clone();    
        meshBackBottomRailModel_Temp.position.set(0, railWidth / 2, params.p_w / 2 - railWidth / 2);
        meshBackBottomRailModel_Temp.updateMatrix();
        meshBackBottomRailModel_Temp.updateMatrixWorld();

        const outerFrontDoorModel = new THREE.Mesh(
            new THREE.ExtrudeGeometry(delModel('leftLeanStorageFront', 'leg'), extrudeSettings(2)),
            new THREE.MeshStandardMaterial({ color: 'brown' })
        )

        const outerbackDoorModel = new THREE.Mesh(
            new THREE.ExtrudeGeometry(delModel('leftLeanStorageBack', 'leg'), extrudeSettings(2)),
            new THREE.MeshStandardMaterial({ color: 'brown' })
        )

        const outerFrontDoorModel_Temp = outerFrontDoorModel.clone();
        outerFrontDoorModel_Temp.rotateY(Math.PI / 2)
        outerFrontDoorModel_Temp.position.set(-1, 0, 0);
        outerFrontDoorModel_Temp.updateMatrixWorld();

        const outerBackDoorModel_Temp = outerbackDoorModel.clone();
        outerBackDoorModel_Temp.rotateY(Math.PI / 2)
        outerBackDoorModel_Temp.position.set(-1, 0, 0);
        outerBackDoorModel_Temp.updateMatrixWorld();
  
        const meshFrontBottomRailModel_CSG = CSG.fromMesh(meshFrontBottomRailModel_Temp);
        const meshBackBottomRailModel_CSG = CSG.fromMesh(meshBackBottomRailModel_Temp);
        const outerFrontDoorModel_CSG = CSG.fromMesh(outerFrontDoorModel_Temp);
        const outerBackDoorModel_CSG = CSG.fromMesh(outerBackDoorModel_Temp);

        const meshFrontBottomRailDelModel_CSG = meshFrontBottomRailModel_CSG.subtract(outerFrontDoorModel_CSG)
        const meshbackBottomRailDelModel_CSG = meshBackBottomRailModel_CSG.subtract(outerBackDoorModel_CSG)

        const CSGFrontBottomRailModel = CSG.toMesh(
            meshFrontBottomRailDelModel_CSG,
            new THREE.Matrix4(),
            new THREE.MeshStandardMaterial({ color: 'blue' })
        )

        const CSGBackBottomRailModel = CSG.toMesh(
            meshbackBottomRailDelModel_CSG,
            new THREE.Matrix4(),
            new THREE.MeshStandardMaterial({ color: 'blue' })
        )

        return {CSGFrontBottomRailModel, CSGBackBottomRailModel}

    }, [params.lean_p_w, params.p_w, const_var.newDoorsArray])
    
    const CSGPillarRailModelArr = useMemo(() => {
        let innerModelArr = [];
        let outerModelArr = [];
        let outerInModelArr = [];
        // let connectModelArr = []
        
        pillarRailDataArr.map((item) => {
            const innerModel = new THREE.Mesh(
                new THREE.ExtrudeGeometry(railModel(railHeight, railWidth), extrudeSettings(item.inner_rail.length)),
                new THREE.MeshStandardMaterial({ color: 'blue' })
            )
            let innerModel_Temp = innerModel.clone();
            innerModel_Temp.rotateX(-Math.PI / 2);
            innerModel_Temp.position.set(item.pos_x, 0, item.inner_rail.pos_z);
            innerModel_Temp.updateMatrixWorld();
            //
            const outerModel = new THREE.Mesh(
                new THREE.ExtrudeGeometry(railModel(railHeight, railWidth), extrudeSettings(item.outer_rail.length)),
                new THREE.MeshStandardMaterial({ color: 'blue' })
            )
            let outerModel_Temp = outerModel.clone();
            outerModel_Temp.rotateX(-Math.PI / 2);
            outerModel_Temp.position.set(item.pos_x, 0, item.outer_rail.pos_z);
            outerModel_Temp.updateMatrixWorld();
            //
            const outerInModel = new THREE.Mesh(
                new THREE.ExtrudeGeometry(railModel(railHeight, railWidth), extrudeSettings(item.outer_in_rail.length)),
                new THREE.MeshStandardMaterial({ color: 'blue' })
            )
            let outerInModel_Temp = outerInModel.clone();
            outerInModel_Temp.rotateX(-Math.PI / 2);
            outerInModel_Temp.position.set(item.pos_x, 0, item.outer_in_rail.pos_z);
            outerInModel_Temp.updateMatrixWorld();

            const innerModel_Mesh_Temp = CSG.fromMesh(innerModel_Temp);
            const innerDoorModel_Mesh_Temp = CSG.fromMesh(innerDoorModel_Temp);
            const outerModel_Mesh_Temp = CSG.fromMesh(outerModel_Temp);
            const outerDoorModel_Mesh_Temp = CSG.fromMesh(outerDoorModel_Temp);
            const outerInModel_Mesh_Temp = CSG.fromMesh(outerInModel_Temp);

            const CSG_InnerModel_Temp = innerModel_Mesh_Temp.subtract(innerDoorModel_Mesh_Temp);
            const CSG_OuterModel_Temp = outerModel_Mesh_Temp.subtract(outerDoorModel_Mesh_Temp);
            const CSG_OuterInModel_Temp = outerInModel_Mesh_Temp.subtract(outerDoorModel_Mesh_Temp);

            const CSG_InnerModel = CSG.toMesh(
                CSG_InnerModel_Temp,
                new THREE.Matrix4(),
                new THREE.MeshStandardMaterial({ color: 'red' })
            )
            const CSG_OuterModel = CSG.toMesh(
                CSG_OuterModel_Temp,
                new THREE.Matrix4(),
                new THREE.MeshStandardMaterial({ color: 'red' })
            )
            const CSG_OuterInModel = CSG.toMesh(
                CSG_OuterInModel_Temp,
                new THREE.Matrix4(),
                new THREE.MeshStandardMaterial({ color: 'red' })
            )

            innerModelArr.push(CSG_InnerModel);
            outerModelArr.push(CSG_OuterModel);
            outerInModelArr.push(CSG_OuterInModel);

            return null;
        })
        const outerDoorModel_Mesh_Temp = CSG.fromMesh(outerDoorModel_Temp);
        const delModelShow = CSG.toMesh(
            outerDoorModel_Mesh_Temp,
            new THREE.Matrix4(),
            new THREE.MeshStandardMaterial({ color: 'red' })
        )
        return { 
            innerModelArr, 
            outerModelArr, 
            outerInModelArr, 
            delModelShow 
        }
    }, [pillarRailDataArr, connectRailDataArr, const_var.newDoorsArray, params.leftLeanStoragePosition, params.lean_p_pos, outerDoorModel_Temp])
    
    useMemo(() => {
        if(params.p_h === leanRoofLeftHeight + params.lean_p_h) setIsEqualLeftHeight(true)
        else setIsEqualLeftHeight(false)
        if(params.p_h === leanRoofRightHeight + params.leanR_p_h) setIsEqualRightHeight(true)
        else setIsEqualRightHeight(false)
        if(params.p_h === leanRoofFrontHeight + params.leanF_p_h) setIsEqualFrontHeight(true)
        else setIsEqualFrontHeight(false)
        
    }, [leanRoofFrontHeight, leanRoofLeftHeight, leanRoofRightHeight, params.p_h, params.lean_p_h, params.leanR_p_h, params.leanF_p_h])
    
    const updateTrussInstancedMesh = useCallback(() => {
        if (ref_upperTrussNormal.current && ref_upperTrussHighlight.current && ref_sideSlopeRail.current && ref_loftPieceRail.current) {
            let index_all = 0;
            pillarRailDataArr.map((item, index_truss) => {
                //----- upper truss -----
                obj_upperTrussNormal.position.set(item.pos_x, item.roof_rail.pos_y, item.roof_rail.pos_z);
                obj_upperTrussNormal.rotation.x = -Math.PI / 2;
                obj_upperTrussNormal.rotateOnAxis(new THREE.Vector3(1, 0, 0), -item.roof_rail.alpha);
                
                if (!!overlapTrussArr && overlapTrussArr.buildingType === 'leftlean' && overlapTrussArr.idArr.includes(index_truss)) {
                    obj_upperTrussNormal.scale.setScalar(0);
                } else {
                    obj_upperTrussNormal.scale.setScalar(1);
                }

                obj_upperTrussNormal.updateMatrix();
                ref_upperTrussNormal.current.setMatrixAt(index_all, obj_upperTrussNormal.matrix);
                //---------------------

                //----- slope rail-----
                obj_sideSlopeRail.position.set(item.pos_x, item.slope_rail.pos_y, item.slope_rail.pos_z);
                
                obj_sideSlopeRail.rotation.x = -Math.PI / 2;
                obj_sideSlopeRail.rotateOnAxis(new THREE.Vector3(1, 0, 0), -item.slope_rail.alpha);

                let slope_rail_overlap = false;

                doorDataArr.map((item_door) => {                            
                    if (item_door.positionX - item_door.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition < item.pos_x + railHeight / 2 &&
                        item_door.positionX + item_door.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition >= item.pos_x - railWidth / 2 &&
                        item_door.positionY + item_door.height / 2 >= item.slope_rail.pos_y
                    ) {
                        slope_rail_overlap = true;
                    }
                    return null;
                })

                if (slope_rail_overlap === true) obj_sideSlopeRail.scale.setScalar(0);
                else  obj_sideSlopeRail.scale.setScalar(1);
                
                obj_sideSlopeRail.updateMatrix();
                ref_sideSlopeRail.current.setMatrixAt(index_all, obj_sideSlopeRail.matrix);
                //---------------------
                
                //----- loft piece rail-----
                obj_loftPieceRail.position.set(item.loft_rail.pos_x, item.loft_rail.pos_y, item.loft_rail.pos_z);
                obj_loftPieceRail.rotation.y = Math.PI / 2;

                if (params.p_r_s !== 1) obj_loftPieceRail.scale.setScalar(0);
                else obj_loftPieceRail.scale.setScalar(1);
                
                obj_loftPieceRail.updateMatrix();
                ref_loftPieceRail.current.setMatrixAt(index_all, obj_loftPieceRail.matrix);
                //---------------------

                index_all ++;
            }) 
            
            ref_upperTrussNormal.current.instanceMatrix.needsUpdate = true;
            ref_sideSlopeRail.current.instanceMatrix.needsUpdate = true;
            ref_loftPieceRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_upperTrussNormal.current, ref_upperTrussHighlight.current, ref_sideSlopeRail.current, params.leftLeanStoragePosition, ref_loftPieceRail.current, pillarRailDataArr, overlapTrussArr, doorDataArr, params.p_r_s, angle, trimOffsetHeight, params.lean_p_pos, roofPatternRailDataArr])

    useLayoutEffect(() => {
        updateTrussInstancedMesh();
    }, [updateTrussInstancedMesh]);
    
    useEffect(() => {
        if (ref_upperTrussHighlight.current) {
            let index_all = 0;
            pillarRailDataArr.map((item, index_truss) => {
                obj_upperTrussHighlight.position.set(item.pos_x, item.roof_rail.pos_y, item.roof_rail.pos_z);
                obj_upperTrussHighlight.rotation.x = -Math.PI / 2;
                obj_upperTrussHighlight.rotateOnAxis(new THREE.Vector3(1, 0, 0), -item.roof_rail.alpha);

                if (!!overlapTrussArr && overlapTrussArr.buildingType === 'leftlean' && overlapTrussArr.idArr.includes(index_truss)) {
                    obj_upperTrussHighlight.scale.setScalar(1);
                } else {
                    obj_upperTrussHighlight.scale.setScalar(0);
                }

                obj_upperTrussHighlight.updateMatrix();
                ref_upperTrussHighlight.current.setMatrixAt(index_all, obj_upperTrussHighlight.matrix);
                //---------------------
                index_all ++;
            })
            
            ref_upperTrussHighlight.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_upperTrussHighlight.current, pillarRailDataArr, overlapTrussArr])

    const updatePillarLegInstancedMesh = useCallback(() => {
        if (ref_outerPillar.current && ref_innerPillar.current && ref_connectRail.current && ref_connectBottomRail.current) {
            let tempCSGLegIdArr = [];
            let isShowLeg = true;
            
            const sideCmpArry = [...const_var.newDoorsArray.filter((item) => wallNameArrForValid.find(subArray => subArray.includes('L_L_L_W')).includes(item.userData.wallName))];
            
            let index_cnt_all = 0;
            pillarRailDataArr.forEach((item, index_truss) => {

                obj_outerPillar.position.set(item.pos_x, 0, item.outer_rail.pos_z);
                obj_outerPillar.rotation.x = -Math.PI / 2;
                obj_innerPillar.position.set(item.pos_x, 0, item.outer_in_rail.pos_z);
                obj_innerPillar.rotation.x = -Math.PI / 2;

                isShowLeg = true
                sideCmpArry.every(element => {
                    if (item.pos_x + railHeight >= (element.positionX - element.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition) && item.pos_x - railHeight <= element.positionX + element.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition) {
                        isShowLeg = false;
                        tempCSGLegIdArr.push(index_truss);
                        return false;
                    }
                    else return true;
                })
                if (!isShowLeg || (!!overlapTrussArr && overlapTrussArr.buildingType === 'leftlean' && overlapTrussArr.idArr.includes(index_truss))) {
                    obj_outerPillar.scale.setScalar(0);
                } else {
                    obj_outerPillar.scale.setScalar(1);
                }

                if (!isShowLeg) {
                    obj_innerPillar.scale.setScalar(0);
                } else {
                    obj_innerPillar.scale.setScalar(1);
                }

                //--- connect leg
                obj_connectBottomRail.position.set(item.pos_x, railWidth / 2, item.outer_in_rail.pos_z)
                if((params.lean_p_e_l == true && (const_var.leanLegstype == "ladder" || const_var.leanLegstype == "zigzag"))) obj_connectBottomRail.scale.setScalar(1);
                else obj_connectBottomRail.scale.setScalar(0)

                sideCmpArry.filter(filterElement => filterElement.userData.compType === "garage_door" || filterElement.userData.compType === "garage_door_frameout" || filterElement.userData.compType === "walkin" || filterElement.userData.compType === "walkin_frameout").forEach(element => {
                    if (item.pos_x + railHeight >= (element.positionX) - element.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition && item.pos_x - railHeight <= (element.positionX) + element.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition) {
                        obj_connectBottomRail.scale.setScalar(0);
                    }
                })

                connectRailDataArr.dataInfoArray.forEach((item_cnt, index_cnt) => {
                    if (index_cnt === 0)  return;

                    obj_connectRail.position.set(item.pos_x, item_cnt.pos_y, item_cnt.pos_z);
                    obj_connectRail.rotation.x = item_cnt.rotationX;
                    if((params.lean_p_e_l == true && (const_var.leanLegstype == "ladder" || const_var.leanLegstype == "zigzag"))) obj_connectRail.scale.setScalar(1);
                    else obj_connectRail.scale.setScalar(0)

                    sideCmpArry.forEach((element) => {
                        if (item.pos_x + railHeight >= (element.positionX) - element.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition && item.pos_x - railHeight <= (element.positionX) + element.width / 2 - params.lean_p_pos - params.leftLeanStoragePosition) {
                            const itemPosY = const_var.leanLegstype === 'ladder' ? item_cnt.pos_y : item_cnt.pos_y - item_cnt.distance
                            
                            if (element.userData.compType === "garage_door" || element.userData.compType === "garage_door_frameout" || element.userData.compType === "walkin" || element.userData.compType === "walkin_frameout") {
                                if (itemPosY - railWidth / 2 <= element.height) {
                                    obj_connectRail.scale.setScalar(0);
                                }
                            } else {
                                if (itemPosY - railWidth / 2 <= element.positionY + element.height / 2 && item_cnt.pos_y + railWidth / 2 >= element.positionY - element.height / 2) {
                                    obj_connectRail.scale.setScalar(0);
                                }
                            }
                        }
                    })       
                    
                    obj_connectRail.updateMatrix();
                    ref_connectRail.current.setMatrixAt(index_cnt_all, obj_connectRail.matrix);

                    index_cnt_all ++;
                })

                obj_connectBottomRail.updateMatrix();
                ref_connectBottomRail.current.setMatrixAt(index_truss, obj_connectBottomRail.matrix);

                //----------

                obj_outerPillar.updateMatrix();
                ref_outerPillar.current.setMatrixAt(index_truss, obj_outerPillar.matrix);
                obj_innerPillar.updateMatrix();
                ref_innerPillar.current.setMatrixAt(index_truss, obj_innerPillar.matrix);
            })

            setCsgLegIdArr(tempCSGLegIdArr)

            ref_outerPillar.current.instanceMatrix.needsUpdate = true;
            ref_innerPillar.current.instanceMatrix.needsUpdate = true;

            ref_connectRail.current.instanceMatrix.needsUpdate = true;
            ref_connectBottomRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_outerPillar.current, ref_innerPillar.current, ref_connectRail.current, ref_connectBottomRail.current, params.leftLeanStoragePosition, params.p_w, const_var.newDoorsArray, pillarRailDataArr, overlapTrussArr, connectRailDataArr, const_var.leanLegstype, params.lean_p_pos, params.lean_p_e_l, const_var.leanLegstype])
    
    useLayoutEffect(() => {
        updatePillarLegInstancedMesh();
    }, [updatePillarLegInstancedMesh]);

    useMemo(() => {
        if (ref_outerPillarHighlight.current) {
            pillarRailDataArr.forEach((item, index_truss) => {

                obj_outerPillarHighlight.position.set(item.pos_x, 0, item.outer_rail.pos_z);
                obj_outerPillarHighlight.rotation.x = -Math.PI / 2;

                if (!!overlapTrussArr && overlapTrussArr.buildingType === 'leftlean' && overlapTrussArr.idArr.includes(index_truss) && !csgLegIdArr.includes(index_truss)) {
                    obj_outerPillarHighlight.scale.setScalar(1);
                } else {
                    obj_outerPillarHighlight.scale.setScalar(0);
                }

                obj_outerPillarHighlight.updateMatrix();
                ref_outerPillarHighlight.current.setMatrixAt(index_truss, obj_outerPillarHighlight.matrix);
            })

            ref_outerPillarHighlight.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_outerPillarHighlight.current, pillarRailDataArr, overlapTrussArr, csgLegIdArr])
  
    return (
        <group name='leanto_legs' rotation={[0, -Math.PI / 2, 0]}>
            {params.leftLeanStoragePosition !== params.lean_p_d - parseInt(params.add_storage) &&
                <group position={[offset, 0, 0]}>
                    {CSGFrontBackPillarRailModelArr.frontModelArr.map((item, index_front) =>
                        <mesh name='lean_backStorage_leg' key={`front-vertical-rail-${index_front}`} geometry={item.geometry} position={[-params.lean_p_d / 2 + parseInt(params.add_storage) - trimThickness, 0, 0]}>
                            <MaterialTruss normalMap={normalMap} envMap={envMap} />
                        </mesh>
                    )}
                    <mesh name='lean_backStorage_leg_base_rail' geometry={CSGFrontBackBottomRailData.CSGFrontBottomRailModel.geometry} position={[(-params.lean_p_d / 2 + parseInt(params.add_storage)) - trimThickness, 0, 0]}>
                        <MaterialTruss normalMap={normalMap} envMap={envMap} />
                    </mesh>
                </group>
            }

            {params.leftLeanStoragePosition !== 0 &&
                <>
                    {CSGFrontBackPillarRailModelArr.backModelArr.map((item, index_back) =>
                        <mesh name='lean_backStorage_leg' key={`back-vertical-rail-${index_back}`} geometry={item.geometry} position={[-params.lean_p_d / 2 + trimThickness, 0, 0]}>
                            <MaterialTruss normalMap={normalMap} envMap={envMap} />
                        </mesh>
                    )}
                    <mesh name='lean_backStorage_leg_base_rail' geometry={CSGFrontBackBottomRailData.CSGBackBottomRailModel.geometry} position={[-params.lean_p_d / 2 + trimThickness, 0, 0]}>
                        <MaterialTruss normalMap={normalMap} envMap={envMap} />
                    </mesh>
                </>
            }

            {CSGPillarRailModelArr.outerModelArr.map((item, index) =>
                csgLegIdArr.includes(index) &&
                <mesh name='outer-pillar-model' geometry={item.geometry}>
                    { !!overlapTrussArr && overlapTrussArr.buildingType === 'leftlean' && overlapTrussArr.idArr.includes(index) ?
                        <MaterialHighlightTruss />
                        :
                        <MaterialTruss normalMap={normalMap} envMap={envMap} />
                    }
                </mesh>
            )}

            {/* --- outer pillar model --- */}
                <instancedMesh ref={ref_outerPillar} args={[null, null, pillarRailDataArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(outerRailLength)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                
                <instancedMesh ref={ref_outerPillarHighlight} args={[null, null, pillarRailDataArr.length]} frustumCulled={false} visible={overlapTrussArr.idArr?.length > 0}>
                    <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(outerRailLength)]} />
                    <MaterialHighlightTruss />
                </instancedMesh>
            {/* ------------------------- */}

            <instancedMesh ref={ref_connectBottomRail} args={[null, null, pillarRailDataArr.length]} frustumCulled={false} visible={params.lean_p_e_l == true && (const_var.leanLegstype == "ladder" || const_var.leanLegstype == "zigzag")}>
                <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(outInDistance)]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            <instancedMesh ref={ref_connectRail} args={[null, null, pillarRailDataArr.length * (connectRailDataArr.dataInfoArray.length - 1)]} frustumCulled={false} visible={params.lean_p_e_l == true && (const_var.leanLegstype == "ladder" || const_var.leanLegstype == "zigzag")}>
                <extrudeGeometry args={[railModel(railHeight, railWidth), const_var.leanLegstype === 'ladder' ? extrudeSettings(connectRailDataArr.lengthLadder) : const_var.leanLegstype === 'zigzag' ? extrudeSettings(connectRailDataArr.lengthZigzag) : 0]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>

            {/* --- inner pillar model --- */}
            <instancedMesh ref={ref_innerPillar} args={[null, null, pillarRailDataArr.length]} frustumCulled={false} visible={(params.lean_p_e_l == true && (const_var.leanLegstype =="double" || const_var.leanLegstype == "ladder" || const_var.leanLegstype == "zigzag"))}>
                <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(innerRailLength)]} />
                <MaterialTruss normalMap={normalMap} envMap={envMap} />
            </instancedMesh>
            {/* ------------------------- */}

            <>
                {/* --- upper truss model --- */}
                <instancedMesh ref={ref_upperTrussNormal} args={[null, null, pillarRailDataArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(verticalRailLength - extraLength)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- slope rail model --- */}
                <instancedMesh ref={ref_sideSlopeRail} args={[null, null, pillarRailDataArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(edgeSlopeRailLength - cbFrontBackWallOffset)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- loft piece rail model --- */}
                <instancedMesh ref={ref_loftPieceRail} args={[null, null, pillarRailDataArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railLoftModel(railWidth, parseInt(params.b_l_t_r_p)), extrudeSettings(railHeight)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}
                
                {/* --- upper truss highlight model --- */}
                <instancedMesh ref={ref_upperTrussHighlight} args={[null, null, pillarRailDataArr.length]} frustumCulled={false} visible={overlapTrussArr.idArr?.length > 0}>
                    <extrudeGeometry args={[railModel(railHeight, railWidth), extrudeSettings(verticalRailLength - extraLength)]} />
                    <MaterialHighlightTruss />
                </instancedMesh>
                {/* ------------------------- */}

            </>
        </group>
    )
}

export default Legs